import React, { useState, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
  Search, Menu, X, ChevronRight, 
  ChevronDown, Hammer 
} from 'lucide-react';
import './Navbar.css';
import logo from './Logo.png';
const API_URL = 'https://api.sohanihandicrafts.com:5010';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [categories, setCategories] = useState([]);
  const [showMore, setShowMore] = useState(false);
  const navigate = useNavigate();

  // 1. Fetch Categories from Backend
  useEffect(() => {
    const fetchCats = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/category/categories?limit=100`);
        // We filter only active categories
        const activeCats = res.data.categories.filter(c => c.status !== false);
        setCategories(activeCats);
      } catch (err) {
        console.error("Navbar fetch error:", err);
      }
    };
    fetchCats();
  }, []);

  // Handle Scroll Effect
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const topCategories = categories.slice(0, 4);
  const moreCategories = categories.slice(4);

  return (
    <>
      <nav className={`nav-art-root ${scrolled ? 'scrolled' : ''}`}>
        <div className="nav-art-container">
          
          {/* LOGO */}
       <div className="nav-art-logo">
  <Link to="/" className="nav-logo-link">
    <div className="nav-art-logo-icon">
      <img src={logo} alt="logo" />
    </div>
    {/* This span will be hidden on desktop and shown on mobile via CSS */}
    <span className="nav-brand-text-mobile">
      Sohani <span>Handicrafts</span>
    </span>
  </Link>
</div>

          {/* DESKTOP LINKS */}
          <ul className="nav-art-links">
            <li><NavLink to="/" end className={({isActive}) => `nav-art-link-item${isActive ? ' active' : ''}`}>Home<span className="nav-art-dot"></span></NavLink></li>
            <li><NavLink to="/about" className={({isActive}) => `nav-art-link-item${isActive ? ' active' : ''}`}>About Us<span className="nav-art-dot"></span></NavLink></li>
            <li><NavLink to="/gallery" className={({isActive}) => `nav-art-link-item${isActive ? ' active' : ''}`}>Infrastructure<span className="nav-art-dot"></span></NavLink></li>
            <li><NavLink to="/trade" className={({isActive}) => `nav-art-link-item${isActive ? ' active' : ''}`}>Trade<span className="nav-art-dot"></span></NavLink></li>
            {/* Map Top 4 Categories */}
            {topCategories.map((cat) => (
              <li key={cat._id}>
                <NavLink to={`/product/${cat._id}`} className={({isActive}) => `nav-art-link-item${isActive ? ' active' : ''}`}>
                  {cat.name}
                  <span className="nav-art-dot"></span>
                </NavLink>
              </li>
            ))}

            {/* "More" Dropdown Logic */}
            {moreCategories.length > 0 && (
              <li 
                className="nav-art-dropdown-trigger"
                onMouseEnter={() => setShowMore(true)}
                onMouseLeave={() => setShowMore(false)}
              >
                <span className="nav-art-link-item">
                  More <ChevronDown size={14} className={`dropdown-chevron ${showMore ? 'rotate' : ''}`} />
                  <span className="nav-art-dot"></span>
                </span>
                
                <AnimatePresence>
                  {showMore && (
                    <motion.div 
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="nav-art-dropdown-menu"
                    >
                      {moreCategories.map(cat => (
                        <Link 
                          key={cat._id} 
                          to={`/product/${cat._id}`} 
                          className="dropdown-item"
                          onClick={() => setShowMore(false)}
                        >
                          {cat.name}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
                
              </li>
              
            )}

          <li><NavLink to="/contact" className={({isActive}) => `nav-art-link-item${isActive ? ' active' : ''}`}>Contact<span className="nav-art-dot"></span></NavLink></li>
          </ul>

          {/* ACTIONS */}
          <div className="nav-art-actions">
            <button className="nav-art-hamburger" onClick={() => setIsOpen(true)}>
              <Menu size={28} />
            </button>
          </div>
        </div>
      </nav>

      {/* MOBILE MENU OVERLAY */}
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            className="nav-art-mobile-menu"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          >
            <div className="nav-art-mobile-header">
              <span className="nav-mobile-brand">Menu</span>
              <button className="nav-art-close-btn" onClick={() => setIsOpen(false)}>
                <X size={28} />
              </button>
            </div>

            <div className="nav-art-mobile-links">
              <NavLink to="/" end onClick={() => setIsOpen(false)} className={({isActive}) => `nav-mobile-link-item${isActive ? ' active' : ''}`}>Home</NavLink>
              
              <div className="mobile-section-label">Categories</div>
              {categories.map((cat) => (
                <Link 
                  key={cat._id} 
                  to={`/products/${cat._id}`} 
                  className="nav-mobile-link-item sub"
                  onClick={() => setIsOpen(false)}
                >
                  {cat.name} <ChevronRight size={14} />
                </Link>
              ))}

              <div className="mobile-section-label">Company</div>
              <NavLink to="/about" onClick={() => setIsOpen(false)} className={({isActive}) => `nav-mobile-link-item${isActive ? ' active' : ''}`}>About Us</NavLink>
              <NavLink to="/contact" onClick={() => setIsOpen(false)} className={({isActive}) => `nav-mobile-link-item${isActive ? ' active' : ''}`}>Contact</NavLink>
              <NavLink to="/trade" onClick={() => setIsOpen(false)} className={({isActive}) => `nav-mobile-link-item${isActive ? ' active' : ''}`}>Trade</NavLink>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;